"use client"

import React, {useEffect, useLayoutEffect, useRef} from "react";
import {motion, useAnimationControls, useReducedMotion} from "framer-motion";
import {cn} from "@/lib/utils";
import Image from "next/image";
import gsap from "gsap";
import {ScrollTrigger} from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

import person1 from "@/public/animatedAssets/person1.png";
import person2 from "@/public/animatedAssets/person2.png";
import person3 from "@/public/animatedAssets/person3.png";
import person4 from "@/public/animatedAssets/person4.svg";
import cursor from "@/public/animatedAssets/cursor.svg";

type State = "collapsed" | "expanded";

export const AnimatedAsset3 = ({
  className = "",
}: {
  className?: string;
}) => {
  const controls = useAnimationControls();
  const prefersReduced = useReducedMotion();
  const stateRef = useRef<State>("collapsed");
  const rootRef = useRef<HTMLDivElement | null>(null);

  const setState = (next: State) => {
    if (stateRef.current === next) return Promise.resolve();
    stateRef.current = next;
    return controls.start(next);
  };

  useEffect(() => {
    controls.set("collapsed");
    stateRef.current = "collapsed";
  }, [controls]);

  useLayoutEffect(() => {
    if (prefersReduced) return;
    const el = rootRef.current;
    if (!el) return;

    const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;

    if (isTouchDevice) {
      const st = ScrollTrigger.create({
        trigger: el,
        start: "top 50%",
        end: "bottom 50%",
        onEnter: () => setState("expanded"),
        onLeave: () => {},
        onEnterBack: () => setState("expanded"),
        onLeaveBack: () => setState("collapsed"),
        invalidateOnRefresh: true,
      });

      return () => st.kill();
    }

    return;
  }, [prefersReduced, controls]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMouseEnter = async () => {
    if (prefersReduced) return;
    await setState("expanded");
  };

  const handleMouseLeave = () => {
    if (prefersReduced) return;
    setState("collapsed");
  };

  const ease = {ease: "circOut", duration: .75};

  const cursorJulian = {
    collapsed: {x: 0,  y: 0,  transition: ease},
    expanded:  {x: 12, y: 12, transition: ease},
  };

  const cursorPhillip = {
    collapsed: {x: 0,  y: 0,  transition: ease},
    expanded:  {x: 12, y: -12, transition: ease},
  };

  const cursorNica = {
    collapsed: {x: 0,  y: 0,  transition: ease},
    expanded:  {x: -12, y: -12, transition: ease},
  };

  const avatars = (i: number) => ({
    collapsed: {x: 0,  y: 0,  transition: ease},
    expanded:  {x: [-20, -8, 8, 20][i], y: 0, transition: ease},
  });

  return (
    <div
      ref={rootRef}
      className={cn(
        "w-full h-[50rem] bg-[#0A1A1D] flex flex-col items-center justify-center px-[2rem] sm:px-[4rem]",
        "relative after:absolute after:h-[2px] after:w-full after:left-0 after:bottom-0 after:bg-[#D9F9B9]",
        className
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <motion.div
        className="absolute top-[6rem] left-1/2 -translate-x-1/2"
        variants={cursorJulian}
        animate={controls}
        initial="collapsed"
      >
        <div className="relative rounded-tr-[30px] rounded-br-[30px] rounded-bl-[30px] rounded-tl-[5px] bg-[#D9F9B9] h-[5rem] px-[2.25rem] text-[#0A1A1D] font-medium leading-none flex items-center justify-center">
          Julian
          <Image
            className="absolute left-0 top-0 -translate-x-[2.125rem] -translate-y-[2.125rem] !w-[2.5rem]"
            src={cursor}
            alt="cursor icon"
          />
        </div>
      </motion.div>

      <motion.div
        className="absolute bottom-[11rem] left-[5rem]"
        variants={cursorPhillip}
        animate={controls}
        initial="collapsed"
      >
        <div className="relative rounded-tr-[30px] rounded-br-[30px] rounded-bl-[30px] rounded-tl-[5px] bg-[#D9F9B9] h-[5rem] px-[2.25rem] text-[#0A1A1D] font-medium leading-none flex items-center justify-center">
          Phillip
          <Image
            className="absolute left-0 top-0 -translate-x-[2.125rem] -translate-y-[2.125rem] !w-[2.5rem]"
            src={cursor}
            alt="cursor icon"
          />
        </div>
      </motion.div>

      <motion.div
        className="absolute bottom-[5rem] right-[7rem]"
        variants={cursorNica}
        animate={controls}
        initial="collapsed"
      >
        <div className="relative rounded-tr-[30px] rounded-br-[30px] rounded-bl-[30px] rounded-tl-[5px] bg-[#D9F9B9] h-[5rem] px-[2.25rem] text-[#0A1A1D] font-medium leading-none flex items-center justify-center">
          Nica
          <Image
            className="absolute left-0 top-0 -translate-x-[2.125rem] -translate-y-[2.125rem] !w-[2.5rem]"
            src={cursor}
            alt="cursor icon"
          />
        </div>
      </motion.div>
      
      <div className="flex flex-row-reverse items-center justify-center mb-[3rem]">
        <motion.div
          variants={avatars(3)}
          animate={controls}
          initial="collapsed"
          className="relative"
        >
          <div className="w-[12rem] relative h-[12rem] rounded-full border-[2px] border-[#D9F9B9]/20 bg-[#0A1A1D] flex items-center justify-center">
            <Image
              className="!w-[10.5rem] !h-[10.5rem] object-cover rounded-full"
              src={person4}
              quality={100}
              alt="portrait"
            />
          </div>
        </motion.div>

        <motion.div
          variants={avatars(2)}
          animate={controls}
          initial="collapsed"
          className="relative"
        >
          <div className="-mr-[4rem] relative w-[12rem] h-[12rem] rounded-full border-[2px] border-[#D9F9B9]/30 bg-[#0A1A1D] flex items-center justify-center">
            <Image
              className="!w-[10.5rem] !h-[10.5rem] object-cover rounded-full"
              src={person3}
              quality={100}
              alt="portrait"
            />
          </div>
        </motion.div>

        <motion.div
          variants={avatars(1)}
          animate={controls}
          initial="collapsed"
          className="relative"
        >
          <div className="-mr-[4rem] relative w-[12rem] h-[12rem] rounded-full border-[2px] border-[#D9F9B9]/60 bg-[#0A1A1D] flex items-center justify-center">
            <Image
              className="!w-[10.5rem] !h-[10.5rem] object-cover rounded-full"
              src={person2}
              quality={100}
              alt="portrait"
            />
          </div>
        </motion.div>

        <motion.div
          variants={avatars(0)}
          animate={controls}
          initial="collapsed"
          className="relative"
        >
          <div className="-mr-[4rem] relative w-[12rem] h-[12rem] rounded-full border-[2px] border-[#D9F9B9] bg-[#0A1A1D] flex items-center justify-center">
            <Image
              className="!w-[10.5rem] !h-[10.5rem] object-cover rounded-full"
              src={person1}
              quality={100}
              alt="portrait"
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
};